import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, Download, LogOut, Menu, Store, UserRound } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useQuery, useQueryClient } from 'react-query';
import { notificationsAPI, storesAPI } from '../../services/api';
import brandDesktopLogoUrl from '../../assets/branding/png/onemart-logo.png';
import brandMobileLogoUrl from '../../assets/branding/logo-sm.png';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { useStoreContextStore } from '../../store/storeContextStore';
import { ALL_STORES_OPTION_VALUE, isAllStoresSelection } from '../../pages/BI/useBiVendorId';

const Header = ({
  onToggleSidebar,
  isWebMode = false,
  isTabletViewport = false,
  tabletViewMode = 'web',
  onChangeTabletViewMode,
  isMobileSidebarOpen = false,
  isDesktopSidebarCollapsed = true,
}) => {
  const { logout, token, isAuthenticated } = useAuthStore();
  const { allowedStores, selectedStore, setStoreContext, setSelectedStore, resetStoreContext } = useStoreContextStore();
  const { canInstall, isInstalled, isInstalling, promptInstall, showIosInstructions } = usePwaInstall();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check token directly from localStorage for reliable auth state
  const authToken = token || localStorage.getItem('token');
  const hasToken = Boolean(authToken || isAuthenticated);
  const selectedStoreInfoId = isAllStoresSelection(selectedStore)
    ? ALL_STORES_OPTION_VALUE
    : (selectedStore?.storeInfoId || selectedStore?.store_info_id || '');
  const storeSelectorOptions = allowedStores.length > 0
    ? allowedStores
    : (selectedStore ? [selectedStore] : []);

  const { data: notificationsData } = useQuery(
    'unreadNotifications',
    () => notificationsAPI.getAll({ isRead: false, limit: 1 }),
    { 
      refetchInterval: 120000,
      refetchIntervalInBackground: false,
      retry: false,
      refetchOnWindowFocus: false,
      enabled: hasToken,
      staleTime: 60000,
      cacheTime: 5 * 60 * 1000,
    }
  );

  const storeContextQuery = useQuery(
    ['storeContext', authToken || 'session', selectedStoreInfoId === ALL_STORES_OPTION_VALUE ? 'all' : (selectedStoreInfoId || 'default')],
    () => storesAPI.getContext(
      selectedStoreInfoId && selectedStoreInfoId !== ALL_STORES_OPTION_VALUE
        ? { storeInfoId: selectedStoreInfoId }
        : undefined
    ),
    {
      enabled: hasToken,
      staleTime: 0,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      retry: 1,
      onSuccess: (response) => {
        const payload = response?.data?.data || {};
        const currentSelected = useStoreContextStore.getState().selectedStore;
        if (isAllStoresSelection(currentSelected) || selectedStoreInfoId === ALL_STORES_OPTION_VALUE) {
          setStoreContext({
            allowedStores: payload.allowedStores || [],
            selectedStore: {
              storeInfoId: ALL_STORES_OPTION_VALUE,
              store_info_id: ALL_STORES_OPTION_VALUE,
              vendorId: 'all',
              vendor_id: 'all',
              name: 'All Stores',
              isAllStores: true,
            },
            storeScope: payload.storeScope || 'all',
          });
          return;
        }
        const nextAllowedStores = payload.allowedStores || [];
        const nextSelectedStore = payload.selectedStore || selectedStore || nextAllowedStores[0] || null;
        setStoreContext({
          allowedStores: nextAllowedStores.length ? nextAllowedStores : (nextSelectedStore ? [nextSelectedStore] : []),
          selectedStore: nextSelectedStore,
          storeScope: payload.storeScope || 'single',
        });
      },
      onError: () => {
        if (!selectedStore && allowedStores.length === 0) {
          resetStoreContext();
        }
      },
    }
  );

  const unreadCount = notificationsData?.data?.unreadCount || 0;
  const showUnreadDot = unreadCount > 0 && location.pathname !== '/notifications';
  const menuButtonActive = isWebMode ? !isDesktopSidebarCollapsed : isMobileSidebarOpen;
  const showStoreSelector = hasToken && storeSelectorOptions.length > 0;
  const showBrandLogo = isDesktopSidebarCollapsed && (isWebMode || (!isMobileSidebarOpen && !showStoreSelector));
  const brandLogoSrc = isWebMode ? brandDesktopLogoUrl : brandMobileLogoUrl;
  const brandLogoClassName = isWebMode
    ? 'h-10 w-auto max-w-[180px] shrink-0 object-contain'
    : 'h-9 w-9 shrink-0 object-contain';

  const handleLogout = async () => {
    await logout();
    resetStoreContext();
    queryClient.removeQueries('storeContext', { exact: false });
    queryClient.removeQueries('unreadNotifications', { exact: false });
    navigate('/login', { replace: true });
  };

  const handleInstall = async () => {
    if (!canInstall) return;
    await promptInstall();
  };

  const handleStoreChange = async (event) => {
    const nextStoreInfoId = event.target.value;
    if (nextStoreInfoId === ALL_STORES_OPTION_VALUE) {
      setSelectedStore({
        storeInfoId: ALL_STORES_OPTION_VALUE,
        store_info_id: ALL_STORES_OPTION_VALUE,
        vendorId: 'all',
        vendor_id: 'all',
        name: 'All Stores',
        isAllStores: true,
      });
      return;
    }

    const nextStore = storeSelectorOptions.find((store) => String(store.storeInfoId || store.store_info_id) === String(nextStoreInfoId));
    if (!nextStore) return;

    setSelectedStore(nextStore);
    try {
      await storesAPI.updateContext({ storeInfoId: nextStoreInfoId });
    } catch (_error) {
      // Keep the local context responsive even if persisting the default store fails.
    }
    await queryClient.invalidateQueries(['storeContext', authToken || 'session'], { exact: false });
  };

  const showInstallButton = !isInstalled;
  const showStoreContextSkeleton = hasToken && storeContextQuery.isLoading && allowedStores.length === 0;

  return (
    <header className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200">
      <div className="flex items-center gap-1 px-1 py-2 sm:gap-3 sm:py-3 sm:px-0 lg:px-0">
        <div className="flex shrink-0 items-center gap-1 sm:gap-3">
          <button
            type="button"
            onClick={onToggleSidebar}
            className={`rounded-lg p-1.5 transition-colors sm:p-2 ${isWebMode ? 'hidden' : ''} ${
              menuButtonActive ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
            }`}
            aria-label="Toggle sidebar"
            aria-expanded={menuButtonActive}
            title="Menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          {showBrandLogo ? (
            <img
              src={brandLogoSrc}
              alt="Foodmandu"
              className={brandLogoClassName}
            />
          ) : null}
          {isTabletViewport ? (
            <div className="hidden items-center rounded-lg border border-gray-200 bg-gray-50 p-1 sm:flex">
              <button
                type="button"
                onClick={() => onChangeTabletViewMode?.('mobile')}
                className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                  tabletViewMode === 'mobile'
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Mobile View
              </button>
              <button
                type="button"
                onClick={() => onChangeTabletViewMode?.('web')}
                className={`rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                  tabletViewMode === 'web'
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Web View
              </button>
            </div>
          ) : null}
        </div>

        {showStoreSelector ? (
          <div className="relative min-w-0 flex-1 sm:ml-auto sm:w-64 sm:flex-none md:w-72 lg:w-80 xl:w-96">
            <Store className="pointer-events-none absolute left-3 top-1/2 hidden h-4 w-4 -translate-y-1/2 text-gray-500 sm:block" />
            <select
              value={selectedStoreInfoId}
              onChange={handleStoreChange}
              disabled={allowedStores.length <= 1 && !isAllStoresSelection(selectedStore)}
              className="input header-store-select h-9 w-full truncate rounded-lg py-1.5 pl-2 pr-7 text-[11px] font-medium leading-tight disabled:cursor-default disabled:bg-gray-50 disabled:text-gray-600 sm:h-10 sm:py-2 sm:pl-9 sm:pr-10 sm:text-sm"
              title={selectedStore?.name || 'Store'}
              aria-label="Selected store"
            >
              {allowedStores.length > 1 ? (
                <option value={ALL_STORES_OPTION_VALUE}>All Stores</option>
              ) : null}
              {storeSelectorOptions.map((store) => {
                const storeInfoId = store.storeInfoId || store.store_info_id;
                return (
                  <option key={storeInfoId} value={storeInfoId}>
                    {store.name || store.storeCode || `Store ${storeInfoId}`}
                  </option>
                );
              })}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500 sm:right-3 sm:h-4 sm:w-4" />
          </div>
        ) : showStoreContextSkeleton ? (
          <div className="relative min-w-0 flex-1 sm:ml-auto sm:w-64 sm:flex-none md:w-72 lg:w-80 xl:w-96">
            <div className="h-9 w-full animate-pulse rounded-lg border border-gray-200 bg-gray-100 sm:h-10" />
          </div>
        ) : null}

        <div className="flex shrink-0 items-center gap-0.5 sm:gap-2">
          {showInstallButton ? (
            <button
              type="button"
              onClick={handleInstall}
              disabled={isInstalling || !canInstall}
              className={`rounded-lg border p-1.5 transition-colors disabled:cursor-default sm:p-2 ${
                canInstall
                  ? 'border-yellow-300 bg-yellow-50 text-yellow-800 hover:bg-yellow-100'
                  : 'border-gray-200 bg-gray-50 text-gray-400'
              }`}
              title={showIosInstructions ? 'Install from Safari share menu' : canInstall ? (isInstalling ? 'Installing app' : 'Install app') : 'Install will appear when available'}
              aria-label={showIosInstructions ? 'Install from Safari share menu' : 'Install app'}
            >
              <Download className="h-4 w-4" />
            </button>
          ) : null}

          <button
            onClick={() => navigate('/notifications')}
            className="relative p-1.5 text-gray-400 transition-colors hover:text-gray-500 sm:p-2"
            aria-label="Notice Board"
            title="Notice Board"
          >
            <Bell className="h-6 w-6" />
            {showUnreadDot && (
              <span className="absolute right-1 top-1 flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-red-600" />
              </span>
            )}
          </button>

          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 rounded-lg px-1.5 py-1 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 sm:px-2"
            aria-label="Profile"
            title="Profile"
          >
            <UserRound className="h-5 w-5" />
            <span className="hidden font-medium xl:inline">Profile</span>
          </button>

          <div className="hidden h-6 w-px bg-gray-300 sm:block" />

          <button
            onClick={handleLogout}
            className="flex items-center gap-2 rounded-lg px-1.5 py-1 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 sm:px-2"
          >
            <LogOut className="h-5 w-5" />
            <span className="hidden font-medium sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
